import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Plus, MessageSquare, UserPlus, Flag } from "lucide-react";
import { btn } from "@/components/common/btn";
import { Input } from "@/components/ui/input";
import { TableRow, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { shortDate, money } from "@/lib/format";
import { addClientContact, updateClientContact, updateProposalPipeline } from "@/lib/api";
import { statusOptions, calcProposalTotals } from "./proposalUtils";
import PillSelect from "@/components/common/PillSelect";

// Same palette as the Work Orders list badges (Submitted = orange, Completed/Won = emerald, Cancelled = red).
export const PROPOSAL_STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  bid_submitted: "bg-orange-100 text-orange-700 border-orange-200",
  won: "bg-emerald-100 text-emerald-700 border-emerald-200",
  lost: "bg-rose-100 text-rose-700 border-rose-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};
// "Won" is intentionally absent — the only path to Won is Convert to WO / Mark as won in the builder.
const INLINE_STATUS_OPTIONS = statusOptions.filter((o) => o.value !== "won").map((o) => ({ ...o, pill: PROPOSAL_STATUS_COLORS[o.value] }));

const fmtPhone = (p) => { if (!p) return ""; const d = p.replace(/\D/g, ""); return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : p; };
const formatPhoneInput = (val) => {
  const d = val.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};
const Dash = () => <span className="text-slate-300">—</span>;
const popTitle = "mb-1.5 text-[11px] font-semibold text-slate-500";

function BidDateCell({ id, bidDate, onChange }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="text-xs text-slate-500 num hover:text-brand hover:underline" title="Change bid due date" data-testid={`pipeline-bid-date-${id}`}>
          {bidDate ? shortDate(bidDate) : <Dash />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="center">
        <div className={popTitle}>Bid Due Date</div>
        <Input type="date" className="h-7 text-xs" value={bidDate || ""} onChange={(e) => { if (e.target.value) onChange(e.target.value); }} data-testid={`pipeline-bid-date-input-${id}`} />
      </PopoverContent>
    </Popover>
  );
}

function ContactCell({ proposal, contacts, activeContactId, activeContact, onPick, onContactAdded }) {
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", role: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editDirty, setEditDirty] = useState(false);
  useEffect(() => { setEditPhone(activeContact?.phone || ""); setEditEmail(activeContact?.email || ""); setEditDirty(false); }, [activeContactId, activeContact?.phone, activeContact?.email]);

  const saveInfo = async () => {
    if (!activeContact || !proposal.client_id) return;
    try {
      await updateClientContact(proposal.client_id, activeContact.id, { phone: editPhone, email: editEmail });
      toast.success("Contact updated"); setEditDirty(false); onContactAdded?.();
    } catch { toast.error("Failed to update contact"); }
  };
  const addNew = async () => {
    if (!newContact.name.trim()) { toast.error("Contact name required"); return; }
    try {
      const res = await addClientContact(proposal.client_id, newContact);
      toast.success("Contact added"); setShowAddForm(false); setNewContact({ name: "", email: "", phone: "", role: "" });
      onPick(res.data.id); onContactAdded?.();
    } catch { toast.error("Failed to add contact"); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {activeContact ? (
          <button type="button" className="block max-w-full truncate text-left text-xs text-slate-700 hover:text-brand hover:underline" title="Change or edit contact" data-testid={`pipeline-contact-edit-${proposal.id}`}>
            {activeContact.name}
          </button>
        ) : (
          <button type="button" className="text-left text-slate-400 hover:text-slate-700" title="Add a contact" data-testid={`pipeline-contact-edit-${proposal.id}`}><Plus className="h-4 w-4" /></button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className={popTitle}>Change Contact</div>
        <Select value={activeContactId} onValueChange={onPick}>
          <SelectTrigger className="h-7 text-xs" data-testid={`pipeline-contact-select-${proposal.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.is_primary ? " (Primary)" : ""}</SelectItem>)}</SelectContent>
        </Select>
        {activeContact && (
          <div className="mt-2 space-y-1 rounded border bg-slate-50 p-2" data-testid={`pipeline-edit-contact-${proposal.id}`}>
            <div className="text-[10px] font-semibold text-slate-400">{activeContact.name}</div>
            <Input className="h-7 text-xs" placeholder="303-333-3333" value={fmtPhone(editPhone)} maxLength={12} onChange={(e) => { setEditPhone(formatPhoneInput(e.target.value)); setEditDirty(true); }} data-testid={`pipeline-edit-phone-${proposal.id}`} />
            <Input className="h-7 text-xs" placeholder="Email" value={editEmail} onChange={(e) => { setEditEmail(e.target.value); setEditDirty(true); }} data-testid={`pipeline-edit-email-${proposal.id}`} />
            {editDirty && <button type="button" className={`${btn("write")} w-full justify-center`} onClick={saveInfo} data-testid={`pipeline-save-contact-${proposal.id}`}>Save</button>}
          </div>
        )}
        {!showAddForm ? (
          <button type="button" className="mt-1.5 inline-flex h-6 w-full items-center gap-1 rounded px-1 text-[11px] font-medium text-brand hover:underline" onClick={() => setShowAddForm(true)} data-testid={`pipeline-add-contact-btn-${proposal.id}`}>
            <UserPlus className="h-3 w-3" /> Add New Contact
          </button>
        ) : (
          <div className="mt-2 space-y-1" data-testid={`pipeline-new-contact-form-${proposal.id}`}>
            <Input className="h-7 text-xs" placeholder="Name *" value={newContact.name} onChange={(e) => setNewContact((p) => ({ ...p, name: e.target.value }))} />
            <Input className="h-7 text-xs" placeholder="Email" value={newContact.email} onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))} />
            <Input className="h-7 text-xs" placeholder="303-333-3333" value={newContact.phone} maxLength={12} onChange={(e) => setNewContact((p) => ({ ...p, phone: formatPhoneInput(e.target.value) }))} />
            <div className="mt-1 flex gap-1">
              <button type="button" className={`${btn("write")} flex-1 justify-center`} onClick={addNew}>Save</button>
              <button type="button" className={btn("nav")} onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ActionCell({ id, nextActionDesc, nextActionDate, actions, onSetDate, onAdd }) {
  const [text, setText] = useState("");
  const [date, setDate] = useState("");
  const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = localISO(new Date());
  const isOverdue = nextActionDate && nextActionDate < today;
  const isUrgent = nextActionDate && nextActionDate <= localISO(new Date(Date.now() + 7 * 86400000));
  const add = () => { if (!text.trim()) return; onAdd(text.trim(), date); setText(""); setDate(""); };
  const tone = isOverdue ? "text-red-600 hover:bg-red-50" : isUrgent ? "text-amber-500 hover:bg-amber-50"
    : nextActionDate ? "text-blue-600 hover:bg-blue-50" : "text-slate-300 hover:bg-slate-100 hover:text-slate-500";
  const tip = nextActionDate
    ? `${nextActionDesc || "Action"} · ${isOverdue ? "overdue " : "due "}${shortDate(nextActionDate)}`
    : nextActionDesc ? nextActionDesc : "No action scheduled — click to add";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" title={tip} className={`inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors ${tone}`}
          data-testid={`pipeline-next-date-${id}`} data-state-tone={isOverdue ? "overdue" : isUrgent ? "urgent" : nextActionDate ? "scheduled" : "none"}>
          <Flag className={`h-3.5 w-3.5 ${nextActionDate ? "fill-current" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="center">
        <div className={`${popTitle} mb-2`}>Actions</div>
        {nextActionDesc && (
          <div className={`mb-2 rounded border px-2 py-1.5 text-xs ${isOverdue ? "border-red-200 bg-red-50" : "border-blue-200 bg-blue-50"}`}>
            <div className="mb-1 text-[10px] text-slate-500">Current</div>
            <span className="mb-1.5 block text-slate-700">{nextActionDesc}</span>
            <div className="flex items-center gap-1">
              <span className="shrink-0 text-[10px] text-slate-400">Due:</span>
              <Input type="date" className={`h-6 flex-1 text-[11px] ${isOverdue ? "border-red-400 text-red-600" : ""}`} value={nextActionDate} onChange={(e) => onSetDate(e.target.value)} data-testid={`pipeline-current-action-date-${id}`} />
            </div>
          </div>
        )}
        <div className="mb-2 max-h-32 space-y-1 overflow-y-auto">
          {actions.length === 0 && !nextActionDesc && <div className="py-2 text-center text-[10px] italic text-slate-300">No actions yet</div>}
          {[...actions].reverse().map((a, i) => (
            // eslint-disable-next-line react/no-array-index-key -- per-proposal action log; no stable id and same text can repeat
            <div key={`act-${i}-${a.date || ""}`} className="rounded bg-slate-50 px-2 py-1.5 text-xs"><span className="text-[10px] font-medium text-slate-400">{shortDate(a.date)}</span><span className="ml-1.5 text-slate-700">{a.text}</span></div>
          ))}
        </div>
        <div className="mb-1.5 flex flex-wrap gap-1">
          {["Follow Up", "Revise Proposal", "Site Visit", "Schedule Meeting"].map((label) => (
            <button key={label} type="button" className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px] text-slate-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              onClick={() => setText(label)} data-testid={`pipeline-quick-action-${label.toLowerCase().replace(/\s/g, "-")}-${id}`}>{label}</button>
          ))}
        </div>
        <div className="space-y-1">
          <Input className="h-7 text-xs" placeholder="New action..." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid={`pipeline-action-input-${id}`} />
          <div className="flex gap-1">
            <Input type="date" className="h-7 flex-1 text-xs" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`pipeline-action-date-input-${id}`} />
            <button type="button" className={btn("write")} onClick={add} data-testid={`pipeline-action-add-${id}`}>Add</button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotesCell({ id, notes, onAdd }) {
  const [text, setText] = useState("");
  const add = () => { if (!text.trim()) return; onAdd(text.trim()); setText(""); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        {notes.length > 0 ? (
          <button type="button" title={`${notes.length} note${notes.length === 1 ? "" : "s"} — read or add`} className="inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800" data-testid={`pipeline-notes-chip-${id}`}>
            <MessageSquare className="h-3.5 w-3.5" /><span className="num" data-testid={`pipeline-notes-count-${id}`}>{notes.length}</span>
          </button>
        ) : (
          <button type="button" title="Add the first note" className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-testid={`pipeline-notes-chip-${id}`}><Plus className="h-4 w-4" /></button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="center">
        <div className={`${popTitle} mb-2`}>Follow-up Notes</div>
        <div className="mb-2 max-h-40 space-y-1 overflow-y-auto">
          {notes.length === 0 && <div className="py-2 text-center text-[10px] italic text-slate-300">No notes yet</div>}
          {[...notes].reverse().map((n, i) => (
            // eslint-disable-next-line react/no-array-index-key -- per-proposal follow-up log; same as actions above
            <div key={`note-${i}-${n.date || ""}`} className="rounded bg-slate-50 px-2 py-1.5 text-xs"><span className="text-[10px] font-medium text-slate-400">{shortDate(n.date)}</span><span className="ml-1.5 text-slate-700">{n.text}</span></div>
          ))}
        </div>
        <div className="flex gap-1">
          <Input className="h-7 flex-1 text-xs" placeholder="Add a note..." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid={`pipeline-note-input-${id}`} />
          <button type="button" className={btn("write")} onClick={add} data-testid={`pipeline-note-add-${id}`}>Add</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// One proposal = one table row; columns mirror the header in pages/Proposals.jsx and the Work Orders list styling.
export function PipelineCard({ proposal, client, onEdit, onContactAdded, onNavigateToClient, onProbabilityChange, onBidDateChange, onStatusChange, selected = false, onToggleSelect }) {
  const [status, setStatus] = useState(proposal.status || "draft");
  useEffect(() => { setStatus(proposal.status || "draft"); }, [proposal.status]);
  const [nextActionDesc, setNextActionDesc] = useState(proposal.next_action_desc || "");
  const [nextActionDate, setNextActionDate] = useState(proposal.next_action_date || "");
  const [bidDate, setBidDate] = useState(proposal.proposal_date || "");
  useEffect(() => { setBidDate(proposal.proposal_date || ""); }, [proposal.proposal_date]);
  const [pipelineActions, setPipelineActions] = useState(proposal.pipeline_actions || []);
  const [pipelineNotes, setPipelineNotes] = useState(proposal.pipeline_notes || []);
  const [contactId, setContactId] = useState(proposal.pipeline_contact_id || "");
  const saveTimer = useRef(null);

  const contacts = client?.contacts || [];
  const primaryContact = contacts.find((c) => c.is_primary) || contacts[0];
  const activeContactId = contactId || primaryContact?.id || "";
  const activeContact = contacts.find((c) => c.id === activeContactId) || primaryContact;
  const gmDollars = useMemo(() => {
    if (!Array.isArray(proposal.scopes) || !proposal.scopes.length) return null;
    try { const t = calcProposalTotals(proposal.scopes, 0, 0); return t.subtotal - (t.costMaterial + t.costFreight + t.costLabor + t.costEquipment + t.costTravel + t.costDisposal); } catch { return null; }
  }, [proposal.scopes]);

  const savePipeline = useCallback((updates) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await updateProposalPipeline(proposal.id, updates); } catch { toast.error("Failed to save"); }
    }, 500);
  }, [proposal.id]);

  const handleStatusChange = async (val) => {
    if (val === "won") { toast.error('Use "Convert to WO" to mark a proposal Won.'); return; }
    const prev = status;
    setStatus(val);
    try { await updateProposalPipeline(proposal.id, { status: val }); onStatusChange?.(proposal.id, val); }
    catch { setStatus(prev); toast.error("Failed to save"); }
  };
  const addAction = (text, date) => {
    const actionDate = date || new Date().toISOString().split("T")[0];
    const history = nextActionDesc ? [...pipelineActions, { date: nextActionDate || actionDate, text: nextActionDesc }] : [...pipelineActions];
    setPipelineActions(history); setNextActionDesc(text); setNextActionDate(actionDate);
    savePipeline({ next_action_desc: text, next_action_date: actionDate, pipeline_actions: history });
  };
  const addNote = (text) => {
    const updated = [...pipelineNotes, { date: new Date().toISOString().split("T")[0], text }];
    setPipelineNotes(updated); savePipeline({ pipeline_notes: updated });
  };

  return (
    <TableRow className={`hover:bg-slate-50 ${selected ? "bg-muted/60" : ""}`} data-testid={`pipeline-card-${proposal.id}`}>
      <TableCell className="text-center">
        <input type="checkbox" checked={selected} onChange={() => onToggleSelect?.(proposal.id)} className="h-4 w-4 cursor-pointer rounded border-slate-300 text-brand focus:ring-brand" aria-label={`Select proposal ${proposal.name}`} data-testid={`pipeline-select-${proposal.id}`} />
      </TableCell>
      <TableCell>
        <button type="button" title={proposal.name} className="block w-full truncate text-left text-xs font-medium text-brand hover:underline" onClick={() => onEdit(proposal)} data-testid={`pipeline-name-${proposal.id}`}>{proposal.name}</button>
      </TableCell>
      <TableCell>
        <div className="flex min-w-0 items-center gap-1">
          <button type="button" title={client?.name || ""} className="truncate text-xs text-slate-600 hover:text-brand hover:underline" onClick={() => onNavigateToClient(proposal.client_id)} data-testid={`pipeline-customer-${proposal.id}`}>{client?.name || "—"}</button>
        </div>
      </TableCell>
      <TableCell className="text-xs text-slate-600" data-testid={`pipeline-am-${proposal.id}`}>{proposal.prepared_by ? proposal.prepared_by.split(" ")[0] : <Dash />}</TableCell>
      <TableCell>
        {status === "won" ? (
          <span className={`inline-flex h-6 w-[90px] items-center justify-center rounded-full border text-[10px] font-medium ${PROPOSAL_STATUS_COLORS.won || "bg-emerald-100 text-emerald-700 border-emerald-200"}`} data-testid={`pipeline-status-${proposal.id}`}>Won</span>
        ) : (
          <PillSelect className="w-[90px] justify-between" value={status} onChange={handleStatusChange} title="Change status" options={INLINE_STATUS_OPTIONS}
            testId={`pipeline-status-${proposal.id}`} optionTestId={(v) => `pipeline-status-opt-${v}`} />
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs font-medium tabular-nums">{money(proposal.value)}</TableCell>
      <TableCell className="text-xs text-slate-500 text-center tabular-nums whitespace-nowrap" data-testid={`pipeline-gm-pct-${proposal.id}`}>{gmDollars != null && proposal.value > 0 ? `${((gmDollars / proposal.value) * 100).toFixed(0)}%` : <Dash />}</TableCell>
      <TableCell className="num"><BidDateCell id={proposal.id} bidDate={bidDate} onChange={(v) => { setBidDate(v); savePipeline({ proposal_date: v }); onBidDateChange?.(proposal.id, v); }} /></TableCell>
      <TableCell>
        <ContactCell proposal={proposal} contacts={contacts} activeContactId={activeContactId} activeContact={activeContact} onContactAdded={onContactAdded}
          onPick={(cId) => { setContactId(cId); savePipeline({ pipeline_contact_id: cId }); }} />
        {activeContact?.email && <a href={`mailto:${activeContact.email}`} className="block truncate text-[10px] text-slate-400 hover:text-brand hover:underline" data-testid={`pipeline-email-${proposal.id}`}>{activeContact.email}</a>}
      </TableCell>
      <TableCell className="text-xs">
        {activeContact?.phone ? <a href={`tel:${activeContact.phone}`} className="whitespace-nowrap text-brand hover:underline num" data-testid={`pipeline-phone-${proposal.id}`}>{fmtPhone(activeContact.phone)}</a> : <span className="text-slate-300" data-testid={`pipeline-phone-${proposal.id}`}>—</span>}
      </TableCell>
      <TableCell className="text-center"><ActionCell id={proposal.id} nextActionDesc={nextActionDesc} nextActionDate={nextActionDate} actions={pipelineActions} onAdd={addAction}
        onSetDate={(v) => { setNextActionDate(v); savePipeline({ next_action_desc: nextActionDesc, next_action_date: v }); }} /></TableCell>
      <TableCell className="text-center"><NotesCell id={proposal.id} notes={pipelineNotes} onAdd={addNote} /></TableCell>
    </TableRow>
  );
}
